/**
 * StuckNudge — gentle bottom-right toast offering a quick tour when the
 * customer has been idle for a while AND hasn't engaged with any tour
 * beacon yet.
 *
 * Design philosophy:
 *   • Tiny, soft, non-blocking. Never an overlay or modal.
 *   • Slides in from bottom-right with a premium ease.
 *   • Two paths out: "Quick tour" (starts the auto-walk) or "✕" (dismiss
 *     for the session). Sessions reset on tab close — by design.
 *   • Mobile: docks bottom-center as a soft pill that won't fight a
 *     thumb-reachable CTA bar.
 *
 * Usage:
 *   <TourProvider> already mounts <StuckNudge /> via composition —
 *   callers only need to set a `stuckTourSequence` on the screen via
 *   the `useStuckNudgeSequence` hook (set on mount, clear on unmount).
 */
import { Sparkles, X } from "lucide-react";
import { useState } from "react";

import useTour from "./useTour";
import useStuckDetection from "./useStuckDetection";
import useFrustrationDetection from "./useFrustrationDetection";
import MayaAvatar from "../../pages/shop/custom-studio/v4/components/MayaAvatar";

export default function StuckNudge({ sequence = [], idleMs = 45_000, enabled = true }) {
    const { shouldShow: idleShow, dismiss: dismissIdle } = useStuckDetection({ idleMs, enabled: enabled && sequence.length > 0 });
    // iter358 — Frustration detection: complementary signal. Same UX
    // (the toast), different trigger (rapid clicks vs idle silence).
    // We OR the two signals so whichever fires first surfaces help.
    const { isFrustrated, reset: resetFrustration } = useFrustrationDetection({
        enabled: enabled && sequence.length > 0,
    });
    const shouldShow = idleShow || isFrustrated;
    const { startAutoWalk } = useTour();
    const [closing, setClosing] = useState(false);

    if (!shouldShow || !sequence.length) return null;

    function handleStartTour() {
        startAutoWalk(sequence, { force: true });
        dismissIdle();
        resetFrustration();
    }
    function handleDismiss() {
        setClosing(true);
        setTimeout(() => {
            dismissIdle();
            resetFrustration();
        }, 220);
    }

    // Slight copy variation depending on which signal triggered it —
    // frustration deserves a more empathic opener than idle.
    const heading = isFrustrated
        ? "Looks tricky — want me to walk you through?"
        : "Need a peek at what's where?";
    const body = isFrustrated
        ? "I noticed a few taps in a row. Quick tour and I'll show you what each section does."
        : "I'll walk you through this screen — takes a minute.";

    return (
        <div
            className="jns-tour-stuck-nudge"
            data-tour-closing={closing ? "true" : "false"}
            data-tour-trigger={isFrustrated ? "frustration" : "idle"}
            data-testid="tour-stuck-nudge"
            role="status"
            aria-live="polite"
        >
            <div className="jns-tour-stuck-nudge-inner">
                <div className="shrink-0">
                    <MayaAvatar size="xs" sparkle testid="stuck-nudge-avatar" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] sm:text-[13px] font-bold text-ink leading-tight">
                        {heading}
                    </p>
                    <p className="text-[11px] sm:text-[11.5px] text-ink-soft leading-snug mt-0.5">
                        {body}
                    </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <button
                        type="button"
                        onClick={handleStartTour}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-ivory text-[11px] font-bold shadow-[0_6px_14px_-6px_rgba(107,44,42,0.6)] hover:-translate-y-0.5 transition-transform whitespace-nowrap"
                        style={{ background: "linear-gradient(135deg, #6B2C2A 0%, #B9663E 60%, #E8A063 100%)" }}
                        data-testid="stuck-nudge-take-tour"
                    >
                        Quick tour <Sparkles size={10} />
                    </button>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-full text-ink-soft hover:text-ink hover:bg-rosegold-mist/40"
                        aria-label="Dismiss"
                        data-testid="stuck-nudge-dismiss"
                    >
                        <X size={13} />
                    </button>
                </div>
            </div>
            <style>{`
                .jns-tour-stuck-nudge {
                    position: fixed;
                    right: 16px; bottom: 20px;
                    z-index: 60;
                    max-width: 360px;
                    background: rgba(255, 248, 238, 0.96);
                    backdrop-filter: blur(14px) saturate(1.05);
                    -webkit-backdrop-filter: blur(14px) saturate(1.05);
                    border: 1.5px solid rgba(185, 102, 62, 0.35);
                    border-radius: 16px;
                    box-shadow:
                        0 18px 44px -20px rgba(80, 30, 30, 0.4),
                        0 6px 14px -8px rgba(180, 100, 60, 0.25);
                    transform: translateY(0);
                    opacity: 1;
                    animation: jnsTourNudgeIn 480ms cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                .jns-tour-stuck-nudge[data-tour-closing="true"] {
                    transition: transform 220ms ease, opacity 220ms ease;
                    transform: translateY(12px);
                    opacity: 0;
                }
                .jns-tour-stuck-nudge-inner {
                    padding: 12px 12px 12px 14px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                @media (max-width: 640px) {
                    .jns-tour-stuck-nudge {
                        right: 12px; left: 12px; bottom: 14px;
                        max-width: none;
                        border-radius: 18px;
                    }
                    .jns-tour-stuck-nudge-inner { padding: 12px; }
                }
                @keyframes jnsTourNudgeIn {
                    0% { opacity: 0; transform: translateY(16px) scale(0.98); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .jns-tour-stuck-nudge { animation: none; }
                }
            `}</style>
        </div>
    );
}
